// Global feedback widget (~/.claude/FEEDBACK.md). Mounted once in the signed-in layout.
// Files a GitHub issue through POST /api/feedback; the token stays on the server.
import { useEffect, useId, useRef, useState } from 'react';
import { api, errorText } from '../lib/api';
import { captureScreenshot, collectContext, downscale } from '../lib/feedback/collect';
import { crumb } from '../lib/feedback/recorder';

type Img = { blob: Blob; url: string; name: string };
const MAX_IMAGES = 4;
const MAX_BYTES = 5_000_000;
// Keep in step with MAX_MESSAGE_CHARS in app/api/feedback.py: the server refuses more, never cuts.
const MAX_CHARS = 30_000;
const COUNTER_FROM = 25_000;

interface Props {
  user: Record<string, unknown> | null;
  routeFileHint: () => string | null;
  getAppState?: () => unknown;
}

export function FeedbackButton({ user, routeFileHint, getAppState }: Props) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState('bug');
  const [severity, setSeverity] = useState('annoying');
  const [images, setImages] = useState<Img[]>([]);
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<{ url?: string; number?: number; deduped?: boolean; error?: string }>({});
  const [imgError, setImgError] = useState('');
  const [dragging, setDragging] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const openRef = useRef(false);
  const imagesRef = useRef<Img[]>([]);
  const ids = useId();

  const chars = Array.from(message).length;
  openRef.current = open;
  imagesRef.current = images;

  function show() {
    restoreRef.current = (document.activeElement as HTMLElement) ?? triggerRef.current;
    crumb('feedback', 'opened feedback');
    setOpen(true);
  }
  function hide() {
    setOpen(false);
    setTimeout(() => (restoreRef.current ?? triggerRef.current)?.focus(), 0);
  }
  const hideRef = useRef(hide);
  hideRef.current = hide;
  const showRef = useRef(show);
  showRef.current = show;

  async function addFiles(files: File[]) {
    setImgError('');
    const room = MAX_IMAGES - imagesRef.current.length;
    if (room <= 0) {
      setImgError(`At most ${MAX_IMAGES} images per report.`);
      return;
    }
    const picked = files.filter((f) => f.type.startsWith('image/'));
    if (picked.length < files.length) setImgError('Only image files can be attached.');
    const out: Img[] = [];
    for (const f of picked.slice(0, room)) {
      try {
        const blob = await downscale(f);
        if (blob.size > MAX_BYTES) {
          setImgError(`${f.name || 'image'} is larger than 5 MB even after resizing; not attached.`);
          continue;
        }
        out.push({ blob, url: URL.createObjectURL(blob), name: f.name || 'pasted image' });
      } catch {
        setImgError(`${f.name || 'image'} could not be read.`);
      }
    }
    if (picked.length > room) setImgError(`At most ${MAX_IMAGES} images per report; extra images were not attached.`);
    // await happened above, outside the state updater
    setImages((prev) => [...prev, ...out].slice(0, MAX_IMAGES));
  }
  const addFilesRef = useRef(addFiles);
  addFilesRef.current = addFiles;

  // Registered once (M-090); current state is read through refs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openRef.current) {
        e.preventDefault();
        hideRef.current();
        return;
      }
      if (e.key.toLowerCase() === 'f' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        if (!openRef.current) showRef.current();
        return;
      }
      if (e.key === 'Tab' && openRef.current && dialogRef.current) {
        const nodes = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (!dialogRef.current.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    const onPaste = (e: ClipboardEvent) => {
      if (!openRef.current) return;
      const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
      if (!files.length) return;
      e.preventDefault();
      void addFilesRef.current(files);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('paste', onPaste);
    };
  }, []);

  function removeImage(i: number) {
    setImages((prev) => {
      URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, j) => j !== i);
    });
  }

  async function screenshot() {
    setImgError('');
    if (imagesRef.current.length >= MAX_IMAGES) {
      setImgError(`At most ${MAX_IMAGES} images per report.`);
      return;
    }
    try {
      const blob = await captureScreenshot();
      if (!blob) {
        setImgError('This browser cannot capture the screen; attach or paste an image instead.');
        return;
      }
      setImages((prev) => [...prev, { blob, url: URL.createObjectURL(blob), name: 'screenshot.png' }].slice(0, MAX_IMAGES));
    } catch (e) {
      setImgError(`Screenshot cancelled or refused (${(e as Error).name || 'error'}).`);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || state === 'sending' || chars > MAX_CHARS) return;
    setState('sending');
    try {
      const form = new FormData();
      form.append('message', message);
      form.append('type', type);
      form.append('severity', severity);
      form.append('context', JSON.stringify(collectContext({ user, routeFileHint: routeFileHint(), appState: getAppState?.() })));
      images.forEach((img, i) => form.append('images', img.blob, `shot-${i + 1}.${img.blob.type === 'image/png' ? 'png' : 'jpg'}`));
      const res = await api<{ number: number; url: string; deduped: boolean }>('/api/feedback', { method: 'POST', form });
      setResult(res);
      setState('done');
      setMessage('');
      images.forEach((img) => URL.revokeObjectURL(img.url));
      setImages([]);
    } catch (err) {
      setResult({ error: errorText(err) });
      setState('error');
    }
  }

  return (
    <>
      <button ref={triggerRef} type="button" className="fb-trigger" onClick={show} aria-label="Send feedback (Ctrl+Shift+F)" title="Send feedback (Ctrl+Shift+F)">
        <span className="sq" aria-hidden="true" /> Feedback
      </button>
      {open && (
        <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && hide()}>
          <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={`${ids}-t`} className="modal fb-dialog">
            <form onSubmit={submit} aria-label="Feedback">
              <header className="modal-head">
                <h2 id={`${ids}-t`}>Send feedback</h2>
                <button type="button" className="btn ghost small" onClick={hide} aria-label="Close feedback">
                  Close
                </button>
              </header>
              <p className="muted small">
                Goes to the private GitHub repository as an issue. The page address, recent errors and your last clicks are attached
                automatically; screenshots may show personal data.
              </p>
              <label className="field">
                <span>What happened, and what did you expect instead?</span>
                <textarea autoFocus required rows={5} value={message} onChange={(e) => setMessage(e.target.value)} name="message" />
                {chars >= COUNTER_FROM && (
                  <small className={chars > MAX_CHARS ? 'msg err' : 'muted'} role="status" data-testid="fb-counter">
                    <span className="num">{chars.toLocaleString('en')}</span> / {MAX_CHARS.toLocaleString('en')} characters
                    {chars > MAX_CHARS ? ': too long to send, shorten it or split it into two reports' : ''}
                  </small>
                )}
              </label>
              <div className="row gap">
                <label className="field grow">
                  <span>Type</span>
                  <select value={type} onChange={(e) => setType(e.target.value)} name="type">
                    <option value="bug">Bug</option>
                    <option value="idea">Idea</option>
                    <option value="confusing">Confusing</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="field grow">
                  <span>Severity</span>
                  <select value={severity} onChange={(e) => setSeverity(e.target.value)} name="severity">
                    <option value="blocker">Blocker</option>
                    <option value="annoying">Annoying</option>
                    <option value="minor">Minor</option>
                  </select>
                </label>
              </div>
              <div
                className={`dropzone${dragging ? ' over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  void addFiles(Array.from(e.dataTransfer.files));
                }}
              >
                <div className="row gap wrap center">
                  <button type="button" className="btn small" onClick={screenshot}>
                    Capture screenshot
                  </button>
                  <label className="btn small file-btn">
                    Attach images
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      aria-label="Attach images"
                      onChange={(e) => {
                        void addFiles(Array.from(e.target.files ?? []));
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <span className="muted small">
                    or drop / paste (Ctrl-V) · <span className="num">{images.length}</span>/{MAX_IMAGES}
                  </span>
                </div>
                {images.length > 0 && (
                  <ul className="thumbs">
                    {images.map((img, i) => (
                      <li key={img.url}>
                        <img src={img.url} alt={`attachment ${i + 1}`} />
                        <button type="button" className="link small" onClick={() => removeImage(i)} aria-label={`Remove attachment ${i + 1}`}>
                          remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {imgError && <p className="msg warn small">{imgError}</p>}
              {state === 'done' && result.url && (
                <p className="msg ok" role="status">
                  {result.deduped ? 'Added to an existing report: ' : 'Filed as '}
                  <a href={result.url} target="_blank" rel="noreferrer">
                    issue #{result.number}
                  </a>
                  .
                </p>
              )}
              {state === 'error' && (
                <p className="msg err" role="alert">
                  Could not send: {result.error}
                </p>
              )}
              <footer className="modal-foot">
                <button type="button" className="btn ghost" onClick={hide}>
                  Close
                </button>
                <button type="submit" className="btn primary" disabled={state === 'sending' || !message.trim() || chars > MAX_CHARS}>
                  {state === 'sending' ? 'Sending…' : 'Send'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
